import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import posthog from '../../lib/posthog';
import {
  Button,
  Spinner,
  Input,
  Select,
  Textarea,
} from '../../components/design-system';
import { COUNTRIES } from '../../lib/countries';
import { compressImage, validateVideo, PHOTO_LIMIT_LABEL, VIDEO_LIMIT_LABEL } from '../../lib/media';
import { MoodboardGrid } from '../../components/ui/MoodboardGrid';
import { HostProfilePreviewModal } from '../../components/host/HostProfilePreviewModal';
import { ProfileStatusBanner } from '../../components/host/profile/ProfileStatusBanner';
import { hostProfileStrength } from '../../lib/hostProfileStrength';
import type { HostProfile, HostSpecialty } from '../../types';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function UploadButton({ label, accept, onFile, loading }: { label: string; accept: string; onFile: (f: File) => void; loading?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }} />
      <Button size="sm" variant="secondary" onClick={() => ref.current?.click()} disabled={loading}>
        {loading ? <Spinner size="sm" /> : label}
      </Button>
    </>
  );
}

export function HostProfileEditor() {
  const { user, profile: authProfile } = useAuth();
  const [profile, setProfile] = useState<HostProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [videoUploading, setVideoUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [billingReady, setBillingReady] = useState(false);
  // Cities a host operates in — links to the admin-managed destinations table.
  const [destinations, setDestinations] = useState<{ id: string; name: string; country: string }[]>([]);
  const [cityIds, setCityIds] = useState<string[]>([]);
  // While per-city trips are on, a host works in one city (an admin can add more).
  const [oneCity, setOneCity] = useState(false);
  const originalCityIds = useRef<string[]>([]);
  const [addCityId, setAddCityId] = useState('');
  const [specialtyOptions, setSpecialtyOptions] = useState<HostSpecialty[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  // The host's own photo lives on `profiles`, not host_profiles — travelers see
  // it on every match card. It was only settable from account Settings, which
  // hosts never find, so it's surfaced here too.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // platform_settings.require_host_payout_ready — when off, matching ignores
  // payout readiness, so the banner must not claim the host is blocked by it.
  const [payoutGateEnforced, setPayoutGateEnforced] = useState(true);

  // Auth events (including the ~hourly TOKEN_REFRESHED) hand back a NEW user
  // object, which used to re-run every load effect and wipe unsaved work.
  // Key the effects on the stable id instead.
  const userId = user?.id ?? null;

  // Snapshot of the last saved state; anything different is unsaved work.
  const savedSnapshot = useRef<string | null>(null);
  const dirtyRef = useRef(false);

  const snapshotOf = (p: HostProfile | null, ids: string[], avatar: string | null) =>
    JSON.stringify([
      p?.bio, p?.headline, p?.taste_notes, p?.country, p?.birth_country,
      p?.specialties, p?.response_time, p?.video_url, p?.moodboard_images, p?.active,
      [...ids].sort(), avatar,
    ]);

  const dirty = savedSnapshot.current !== null
    && savedSnapshot.current !== snapshotOf(profile, cityIds, avatarUrl);
  dirtyRef.current = dirty;

  // Warn before a reload/close discards unsaved edits. There is no autosave,
  // and a long bio is easy to lose.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!userId) return;
    void (async () => {
      const [{ data: dests }, { data: links, error: linksError }, { data: rule }] = await Promise.all([
        supabase.from('destinations').select('id, name, country').eq('active', true).order('sort_order'),
        // Ordered so the first entry — the label city — is stable between
        // sessions rather than whatever order Postgres happened to return.
        supabase.from('host_destinations').select('destination_id').eq('host_id', userId).order('created_at'),
        supabase.from('platform_settings').select('per_city_trips').limit(1).maybeSingle(),
      ]);
      setOneCity((rule as { per_city_trips?: boolean } | null)?.per_city_trips === true);
      setDestinations((dests as { id: string; name: string; country: string }[]) ?? []);
      if (linksError) { setLoadError(true); return; }
      // Never overwrite edits in progress (an auth token refresh re-runs this).
      if (dirtyRef.current) return;
      const ids = ((links as { destination_id: string }[]) ?? []).map((l) => l.destination_id);
      setCityIds(ids);
      originalCityIds.current = ids;
    })();
  }, [userId]);

  useEffect(() => {
    void supabase
      .from('platform_settings')
      .select('require_host_payout_ready')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setPayoutGateEnforced((data as { require_host_payout_ready: boolean }).require_host_payout_ready !== false);
      });
  }, []);

  useEffect(() => {
    void supabase
      .from('host_specialties')
      .select('*')
      .eq('active', true)
      .order('sort_order')
      .then(({ data }) => setSpecialtyOptions((data as HostSpecialty[]) ?? []));
  }, []);

  useEffect(() => {
    if (!userId) return;
    void supabase
      .from('host_profiles')
      .select('*')
      .eq('profile_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        // A swallowed load error used to render a BLANK profile, which the old
        // save path then tried to INSERT over an existing row.
        if (error) { setLoadError(true); setLoading(false); return; }
        // An auth token refresh (~hourly, and on tab refocus) re-runs this
        // effect. Without this guard it overwrote every unsaved edit —
        // including media already uploaded to storage.
        if (dirtyRef.current) { setLoading(false); return; }
        const hp = data as HostProfile | null;
        setProfile(hp ? {
          ...hp,
          moodboard_images: hp.moodboard_images ?? [],
          response_time: hp.response_time ?? '~1 day',
        } : {
          id: '',
          profile_id: userId,
          bio: null,
          headline: null,
          taste_notes: null,
          city: null,
          country: null,
          birth_country: null,
          specialties: null,
          active: true,
          video_url: null,
          moodboard_images: [],
          response_time: '~1 day',
          consecutive_missed_trips: 0,
          suspended_for_missed_trips_at: null,
          created_at: '',
          updated_at: '',
        });
        setLoading(false);
      });
  }, [userId]);

  // Establish the baseline once everything has loaded, so `dirty` measures real
  // edits rather than the initial hydration.
  useEffect(() => {
    if (loading || !profile || savedSnapshot.current !== null) return;
    savedSnapshot.current = snapshotOf(profile, cityIds, avatarUrl);
  }, [loading, profile, cityIds, avatarUrl]);

  useEffect(() => {
    // Only adopt the auth copy until the host uploads one here, otherwise a
    // later auth refresh reverts the photo they just chose.
    setAvatarUrl((prev) => prev ?? authProfile?.avatar_url ?? null);
  }, [authProfile]);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('host_billing_details')
      .select('host_id')
      .eq('host_id', userId)
      .maybeSingle()
      .then(({ data }) => setBillingReady(!!data));
  }, [userId]);

  async function handleAvatarUpload(file: File) {
    if (!user) return;
    // Its own error slot: this used to render down in the Moodboard section,
    // often below the fold and under a different heading.
    setAvatarError(null);
    setAvatarUploading(true);
    const optimized = await compressImage(file);
    const path = `${user.id}/${Date.now()}-${optimized.name}`;
    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(path, optimized, { upsert: true });
    if (!error && data) {
      const { data: pub } = supabase.storage.from('avatars').getPublicUrl(data.path);
      const { error: updateError } = await supabase.from('profiles')
        .update({ avatar_url: pub.publicUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      // Previously ignored — the new photo appeared, then vanished on reload.
      if (updateError) {
        setAvatarError('Your photo uploaded but could not be saved. Please try again.');
      } else {
        setAvatarUrl(pub.publicUrl);
      }
    } else {
      setAvatarError('Could not upload your photo. Please try another image.');
    }
    setAvatarUploading(false);
  }

  function update<K extends keyof HostProfile>(key: K, value: HostProfile[K]) {
    setProfile((prev) => prev ? { ...prev, [key]: value } : prev);
  }

  function toggleSpecialty(name: string) {
    const current = profile?.specialties ?? [];
    update('specialties', current.includes(name) ? current.filter((s) => s !== name) : [...current, name]);
  }

  async function handleSave() {
    if (!user || !profile) return;
    setSaveState('saving');
    setSaveError(null);

    // One transactional RPC replaces the old three separate browser writes
    // (delete links / insert links / update-or-insert profile), which could
    // half-apply and then wedge every later save. See the save_host_profile
    // migration for the defects this closes.
    const { data, error } = await supabase.rpc('save_host_profile', {
      p_bio: profile.bio,
      p_headline: profile.headline,
      p_taste_notes: profile.taste_notes,
      p_country: profile.country,
      p_birth_country: profile.birth_country,
      p_specialties: profile.specialties ?? [],
      p_response_time: profile.response_time,
      p_video_url: profile.video_url,
      p_moodboard_images: profile.moodboard_images ?? [],
      p_destination_ids: cityIds,
      p_primary_destination_id: cityIds[0] ?? null,
      p_active: profile.active,
    });

    if (error) {
      console.error('save_host_profile failed:', error);
      setSaveError(error.message || 'Could not save your profile. Please try again.');
      setSaveState('error');
      return;
    }

    const saved = data as HostProfile | null;
    if (saved) {
      setProfile({
        ...saved,
        moodboard_images: saved.moodboard_images ?? [],
        response_time: saved.response_time ?? '~1 day',
      });
    }
    originalCityIds.current = cityIds;
    savedSnapshot.current = snapshotOf(saved ?? profile, cityIds, avatarUrl);
    posthog.capture('host_profile_saved', {
      destination_count: cityIds.length,
      specialty_count: profile.specialties?.length ?? 0,
      is_active: profile.active !== false,
    });
    setSaveState('saved');
    setTimeout(() => setSaveState('idle'), 2500);
  }

  async function handleVideoUpload(file: File) {
    if (!user) return;
    setVideoError(null);
    setVideoUploading(true);

    const validation = await validateVideo(file);
    if (!validation.ok) {
      setVideoError(validation.error ?? 'This video doesn\'t meet our limits.');
      setVideoUploading(false);
      return;
    }

    const path = `${user.id}/${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage.from('host-videos').upload(path, file, { upsert: true });
    if (!error && data) {
      const { data: url } = supabase.storage.from('host-videos').getPublicUrl(data.path);
      update('video_url', url.publicUrl);
    } else if (error) {
      setVideoError('Upload failed. Please try again with a smaller file.');
    }
    setVideoUploading(false);
  }

  async function handleMoodboardUpload(file: File) {
    if (!user || !profile) return;
    if ((profile.moodboard_images ?? []).length >= 9) return;
    setImageError(null);
    setImageUploading(true);

    const optimized = await compressImage(file);
    const path = `${user.id}/${Date.now()}-${optimized.name}`;
    const { data, error } = await supabase.storage.from('host-moodboard').upload(path, optimized, { upsert: false });
    if (!error && data) {
      const { data: url } = supabase.storage.from('host-moodboard').getPublicUrl(data.path);
      update('moodboard_images', [...(profile.moodboard_images ?? []), url.publicUrl]);
    } else if (error) {
      setImageError('Upload failed. Please try a different photo.');
    }
    setImageUploading(false);
  }

  function removeMoodboardImage(index: number) {
    if (!profile) return;
    update('moodboard_images', profile.moodboard_images.filter((_, i) => i !== index));
  }

  if (loading || !profile) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (!profile) return null;

  const strength = hostProfileStrength(profile, billingReady, cityIds.length, avatarUrl, payoutGateEnforced);
  const moodboard = profile.moodboard_images ?? [];
  const canAddImage = moodboard.length < 9;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">My Profile</h1>
            <p className="text-app-muted text-sm">How Alma matches you, and what travelers see when they choose.</p>
          </div>
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <Button variant="secondary" onClick={() => setPreviewOpen(true)}>
              Preview as traveler
            </Button>
            <Button onClick={() => void handleSave()} disabled={saveState === 'saving' || !dirty}>
              {saveState === 'saving' ? <Spinner size="sm" className="text-ink" /> :
               saveState === 'saved' ? 'Saved ✓' :
               dirty ? 'Save profile' : 'Saved'}
            </Button>
          </div>
        </div>

        <ProfileStatusBanner strength={strength} />

        {loadError && (
          <div className="rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 mb-6" role="alert">
            <p className="text-sm text-crimson">
              We couldn't load your profile just now. Please refresh before editing — saving from
              here could overwrite what's already saved.
            </p>
          </div>
        )}

        {saveError && (
          <div className="rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 mb-6" role="alert">
            <p className="text-sm text-crimson">{saveError}</p>
          </div>
        )}

        {dirty && !saveError && (
          <div className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-2.5 mb-6">
            <p className="text-xs text-app-fg-2">You have unsaved changes.</p>
          </div>
        )}

        <div className="flex flex-col gap-8">
        {/* Basic info */}
        <section className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-app-fg">About you</h2>

          {/* Profile photo — this is the face travelers see on your match card */}
          <div className="flex items-center gap-4">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Your profile" className="w-16 h-16 rounded-full object-cover border border-app-border" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-app-bg border border-dashed border-app-border flex items-center justify-center">
                <i className="ti ti-user text-app-muted text-xl" />
              </div>
            )}
            <div className="min-w-0">
              <UploadButton
                label={avatarUrl ? 'Change photo' : 'Add profile photo'}
                accept="image/*,.heic,.heif"
                onFile={(f) => void handleAvatarUpload(f)}
                loading={avatarUploading}
              />
              <p className="text-xs text-app-muted mt-1.5">Travelers see this on your match card.</p>
              {avatarError && <p className="text-xs text-crimson mt-1">{avatarError}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Headline" value={profile.headline ?? ''} onChange={(e) => update('headline', e.target.value)} placeholder="The local who knows Porto inside-out" />
            <Select
              label="Currently based in"
              options={COUNTRIES}
              placeholder="Select a country"
              value={profile.country ?? ''}
              onChange={(e) => update('country', e.target.value || null)}
            />
            <Input label="Replies in" value={profile.response_time} onChange={(e) => update('response_time', e.target.value)} placeholder="~1 day" />
          </div>

          {/* Cities the host operates in (can be more than one) */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-app-fg-2">
              {oneCity ? 'City you host in' : 'Cities you host in'}{' '}
              {!oneCity && <span className="text-app-muted font-normal">(you can add more than one)</span>}
            </label>
            {cityIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {cityIds.map((id) => {
                  const d = destinations.find((x) => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1.5 rounded-pill bg-app-bg border border-app-border pl-3 pr-2 py-1 text-sm text-app-fg">
                      {d ? d.name : 'Unknown city'}
                      <button
                        type="button"
                        aria-label={`Remove ${d?.name ?? 'city'}`}
                        onClick={() => setCityIds((ids) => ids.filter((x) => x !== id))}
                        className="w-5 h-5 rounded-full flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-app-border transition-colors"
                      >
                        <i className="ti ti-x text-xs" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {(() => {
              const available = destinations.filter((d) => !cityIds.includes(d.id));
              // One city: remove the current one to pick another. Need a second? An admin sets it.
              if (oneCity && cityIds.length > 0) {
                return <p className="text-xs text-app-muted">You host in one city. To change it, remove it and pick another. To cover a second city, ask an admin.</p>;
              }
              if (available.length === 0 && destinations.length === 0) {
                return <p className="text-xs text-app-muted">No destinations are set up yet — ask an admin to add cities under Admin → Destinations.</p>;
              }
              return (
                <Select
                  placeholder={available.length ? 'Add a city…' : 'All available cities added'}
                  options={available.map((d) => ({ value: d.id, label: `${d.name}, ${d.country}` }))}
                  value={addCityId}
                  disabled={available.length === 0}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (id) setCityIds((ids) => [...ids, id]);
                    setAddCityId('');
                  }}
                />
              );
            })()}
          </div>
          {/* Pause — `active` gates ONLY alma-finalize's candidate query, so
              pausing stops new matches without touching live trips. */}
          <div className="flex items-start justify-between gap-4 rounded-xl border border-app-border bg-app-bg px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-app-fg">Accepting new travelers</p>
              <p className="text-xs text-app-muted mt-0.5">
                {profile.active !== false
                  ? "Alma can match you with new travelers. Turn this off to pause — trips you've already accepted aren't affected."
                  : "You're paused, so Alma won't propose you to anyone new. Trips you've already accepted continue as normal."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={profile.active !== false}
              aria-label="Accepting new travelers"
              onClick={() => update('active', !(profile.active !== false))}
              className={`relative w-11 h-6 rounded-full shrink-0 transition-colors ${
                profile.active !== false ? 'bg-gold' : 'bg-app-border'
              }`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-app-bg transition-[left] ${
                  profile.active !== false ? 'left-[22px]' : 'left-0.5'
                }`}
              />
            </button>
          </div>

          <Textarea
            label="Bio"
            hint="Shown to travelers, and read by Alma when matching you."
            value={profile.bio ?? ''}
            onChange={(e) => update('bio', e.target.value)}
            rows={4}
            placeholder="Tell travelers who you are, what drives you, what you love about your city…"
          />

          {/* Specialties — a fixed list (managed under Admin → Host Specialties)
              hosts toggle on/off, shown as chips to travelers and read by Alma
              when matching. Keeps wording consistent across hosts. */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-app-fg-2">
              Specialties <span className="text-app-muted font-normal">(shown to travelers, helps Alma match you)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {specialtyOptions.map((opt) => {
                const selected = (profile.specialties ?? []).includes(opt.name);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    // Selection was signalled by background colour alone, which
                    // told screen readers and colour-blind users nothing.
                    aria-pressed={selected}
                    onClick={() => toggleSpecialty(opt.name)}
                    className={`inline-flex items-center gap-1.5 rounded-pill border px-3 py-1.5 text-sm transition-colors ${
                      selected
                        ? 'bg-gold text-ink border-gold'
                        : 'bg-app-bg border-app-border text-app-fg-2 hover:text-app-fg hover:border-app-fg-2'
                    }`}
                  >
                    {selected && <i className="ti ti-check text-xs" aria-hidden="true" />}
                    {opt.name}
                  </button>
                );
              })}
            </div>
            {/* A previously-selected specialty an admin has since deactivated —
                keep it visible and removable, just not re-selectable. */}
            {(profile.specialties ?? []).filter((s) => !specialtyOptions.some((o) => o.name === s)).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {(profile.specialties ?? []).filter((s) => !specialtyOptions.some((o) => o.name === s)).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-pill bg-app-bg border border-dashed border-app-border pl-3 pr-2 py-1 text-sm text-app-muted">
                    {s} <span className="text-[10px]">(no longer offered)</span>
                    <button
                      type="button"
                      aria-label={`Remove ${s}`}
                      onClick={() => toggleSpecialty(s)}
                      className="w-5 h-5 rounded-full flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-app-border transition-colors"
                    >
                      <i className="ti ti-x text-xs" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Taste notes — private to Alma, never shown to travelers directly.
              Describes what kind of traveler this host is a great fit for. */}
          <Textarea
            label="What kind of traveler are you great for?"
            hint="Used by Alma to match you — never shown to travelers."
            value={profile.taste_notes ?? ''}
            onChange={(e) => update('taste_notes', e.target.value)}
            rows={3}
            placeholder="e.g. Curious first-timers who want to eat where locals eat, not rush through checklists…"
          />
        </section>

        {/* Intro video */}
        <section className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-app-fg">Intro video</h2>
              <p className="text-xs text-app-muted mt-0.5">A short clip for travelers to get to know you. Not trip-specific.</p>
              <p className="text-xs text-app-muted mt-0.5">{VIDEO_LIMIT_LABEL}</p>
            </div>
            <UploadButton label="Upload video" accept="video/*" onFile={(f) => void handleVideoUpload(f)} loading={videoUploading} />
          </div>
          {videoError && <p className="text-xs text-crimson">{videoError}</p>}
          {profile.video_url ? (
            <div className="rounded-xl overflow-hidden bg-black aspect-video">
              <video src={profile.video_url} controls playsInline className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="rounded-xl bg-app-bg border border-dashed border-app-border aspect-video flex flex-col items-center justify-center gap-2">
              <p className="text-xs text-app-muted">No video yet — upload one above</p>
            </div>
          )}
          {profile.video_url && (
            <Button size="sm" variant="ghost" className="self-start text-crimson hover:text-crimson" onClick={() => update('video_url', null)}>
              Remove video
            </Button>
          )}
        </section>

        {/* Moodboard */}
        <section className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-app-fg">How you see the city</h2>
              <p className="text-xs text-app-muted mt-0.5">Up to 9 images — places, vibes, things you love.</p>
              <p className="text-xs text-app-muted mt-0.5">{PHOTO_LIMIT_LABEL}</p>
            </div>
            {canAddImage && (
              <UploadButton label={`Add photo (${moodboard.length}/9)`} accept="image/*,.heic,.heif" onFile={(f) => void handleMoodboardUpload(f)} loading={imageUploading} />
            )}
          </div>
          {imageError && <p className="text-xs text-crimson">{imageError}</p>}

          {moodboard.length > 0 && (
            <>
              <MoodboardGrid images={moodboard} alt="Moodboard" />
              {/* Remove buttons below grid */}
              <div className="flex flex-wrap gap-2">
                {moodboard.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => removeMoodboardImage(i)}
                    className="text-[11px] text-crimson hover:text-crimson/80 border border-crimson/30 rounded-full px-2 py-0.5 transition-colors"
                  >
                    Remove #{i + 1}
                  </button>
                ))}
              </div>
            </>
          )}

          {moodboard.length === 0 && (
            <div className="rounded-xl bg-app-bg border border-dashed border-app-border h-40 flex flex-col items-center justify-center gap-2">
              <p className="text-xs text-app-muted">No images yet</p>
            </div>
          )}
        </section>

        </div>
      </div>

      {/* Phone action bar — Save stays in reach however far the form scrolls */}
      <div className="md:hidden sticky bottom-0 z-20 bg-app-card border-t border-app-border px-4 pt-3 pb-safe-or-4 flex gap-2.5">
        <Button variant="secondary" onClick={() => setPreviewOpen(true)} className="flex-1 min-h-[44px]">
          Preview
        </Button>
        <Button onClick={() => void handleSave()} disabled={saveState === 'saving' || !dirty} className="flex-1 min-h-[44px]">
          {saveState === 'saving' ? <Spinner size="sm" className="text-ink" /> :
           saveState === 'saved' ? 'Saved ✓' :
           dirty ? 'Save profile' : 'Saved'}
        </Button>
      </div>

      {previewOpen && (
        <HostProfilePreviewModal
          hostName={authProfile?.full_name ?? 'You'}
          hostAvatar={avatarUrl}
          profile={profile}
          cities={cityIds.map((id) => destinations.find((d) => d.id === id)?.name).filter((n): n is string => !!n)}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

export default HostProfileEditor;
